import { makeAutoObservable, runInAction } from "mobx";
import dashboardStore from "./dashboardStore";
import { ICharacterDto, TrainingCalendarDaySummary } from "../shared/interfaces";
import CharactersApiService from "../services/api/CharactersApiService";
import { mapCharacterList } from "../services/api/adapters/characterAdapter";

const KEY_CHAR = "app_selectedCharacterId";
const KEY_LEVEL = "app_currentLevel";
const KEY_RECORD = "app_currentRecordId";
const KEY_QUEST_ID = "app_currentQuestId";
const KEY_QUEST_OBJ = "app_currentQuestObj";
interface LevelRecord {
    recordId: string;
    level: string;
    typeEx: string;
    name: string;
    createdAt: string;
}
const ls = {
    get: (k: string) => {
        try {
            return localStorage.getItem(k);
        }
        catch {
            return null;
        }
    },
    set: (k: string, v: string) => {
        try {
            localStorage.setItem(k, v);
        }
        catch { }
    },
    del: (k: string) => {
        try {
            localStorage.removeItem(k);
        }
        catch { }
    },
    getObj: <T>(k: string): T | null => {
        try {
            const v = localStorage.getItem(k);
            return v ? (JSON.parse(v) as T) : null;
        }
        catch {
            return null;
        }
    },
    setObj: (k: string, v: any) => {
        try {
            localStorage.setItem(k, JSON.stringify(v));
        }
        catch { }
    },
};
const NEW_RUN_QUEST_MODES = new Set([
    "energy_trail",
    "stabilizers",
    "hot_cold_radar",
    "zone_compression",
]);
const normalizeQuestType = (value?: any): string => String(value ?? "")
    .trim()
    .toLowerCase();
const parseJsonObject = (raw: any): Record<string, any> | null => {
    if (!raw)
        return null;
    if (typeof raw === "object")
        return raw as Record<string, any>;
    try {
        const parsed = JSON.parse(String(raw));
        return parsed && typeof parsed === "object" ? parsed : null;
    }
    catch {
        return null;
    }
};
const firstValue = (obj: any, keys: string[]) => {
    for (const key of keys) {
        if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "")
            return obj[key];
    }
    return undefined;
};
const getRunQuestModeFromRecord = (q: any): string => {
    const candidates = [
        firstValue(q, [
            "run_challenge_mode",
            "RunChallengeMode",
            "challenge_mode",
            "ChallengeMode",
            "run_mode",
            "RunMode",
            "quest_mode",
            "QuestMode",
            "activity_mode",
            "ActivityMode",
            "mode",
            "Mode",
            "task_type",
            "TaskType",
            "type",
            "Type",
        ]),
    ];
    for (const source of [
        firstValue(q, [
            "requirements",
            "Requirements",
            "requirement_json",
            "RequirementJson",
        ]),
        firstValue(q, ["telemetry", "Telemetry", "timedPoints", "TimedPoints"]),
        firstValue(q, ["progress", "Progress"]),
    ]) {
        const parsed = parseJsonObject(source);
        if (!parsed)
            continue;
        const nestedObjects = [
            parsed,
            parseJsonObject(parsed.requirements ?? parsed.Requirements),
            parseJsonObject(parsed.progress ?? parsed.Progress),
            parseJsonObject(parsed.player_progress ??
                parsed.playerProgress ??
                parsed.PlayerProgress),
            parseJsonObject(parsed.team_progress ?? parsed.teamProgress ?? parsed.TeamProgress),
        ].filter(Boolean) as Record<string, any>[];
        for (const nested of nestedObjects) {
            candidates.push(firstValue(nested, [
                "run_challenge_mode",
                "RunChallengeMode",
                "challenge_mode",
                "ChallengeMode",
                "run_mode",
                "RunMode",
                "quest_mode",
                "QuestMode",
                "activity_mode",
                "ActivityMode",
                "mode",
                "Mode",
                "task_type",
                "TaskType",
                "type",
                "Type",
            ]));
        }
    }
    for (const candidate of candidates) {
        const normalized = normalizeQuestType(candidate);
        if (NEW_RUN_QUEST_MODES.has(normalized))
            return normalized;
    }
    return "";
};
const getCoopMetaFromRecord = (q: any): Record<string, any> => {
    const meta: Record<string, any> = {};
    const sources = [
        q,
        parseJsonObject(firstValue(q, [
            "requirements",
            "Requirements",
            "requirement_json",
            "RequirementJson",
        ])),
        parseJsonObject(firstValue(q, ["progress", "Progress"])),
    ].filter(Boolean) as Record<string, any>[];
    for (const source of sources) {
        const nestedSources = [
            source,
            parseJsonObject(source.player_progress ??
                source.playerProgress ??
                source.PlayerProgress),
            parseJsonObject(source.team_progress ?? source.teamProgress ?? source.TeamProgress),
        ].filter(Boolean) as Record<string, any>[];
        for (const nested of nestedSources) {
            for (const key of [
                "guild_coop",
                "coop",
                "coop_mode",
                "coop_session_id",
                "guild_coop_session_id",
                "team_status",
                "team_completed",
                "team_failed",
                "team_result_authoritative",
                "host_character_id",
                "guest_character_id",
                "counterpart_character_id",
                "player_role",
            ]) {
                if (nested[key] !== undefined &&
                    nested[key] !== null &&
                    nested[key] !== "") {
                    meta[key] = nested[key];
                }
            }
        }
    }
    return meta;
};
const isGuildCoopRecord = (q: any): boolean => {
    const meta = getCoopMetaFromRecord(q);
    return Boolean(meta.guild_coop === true ||
        meta.coop === true ||
        meta.coop_mode === true ||
        meta.coop_session_id ||
        meta.guild_coop_session_id ||
        String(q?.TaskID ?? q?.task_id ?? "").startsWith("coop:"));
};
class CharactersStore {
    public isLoading = false;
    public characters: ICharacterDto[] = [];
    public selectedCharacter: ICharacterDto | null = null;
    public isDeletingCharacter = false;
    public characterDeleteError: string | null = null;
    public characterDataCSV: any[] = [];
    public characterDataGesture: any[] = [];
    public statResult: any[] = [];
    public currentLevel = "";
    public currentAttempt = 1;
    public selectedCharacterDate: string | null = null;
    public currentCreatedAt: string | null = null;
    public searchValue = "";
    public availableLevels: LevelRecord[] = [];
    public completedQuests: any[] = [];
    public minigameSessions: any[] = [];
    public trainingCalendarSummary: Record<string, TrainingCalendarDaySummary> = {};
    public isTrainingCalendarLoading = false;
    public trainingCalendarError: string | null = null;
    private trainingCalendarRequestKey = '';
    private levelsRequestKey = '';
    private completedQuestsRequestKey = '';
    private minigameSessionsRequestKey = '';
    public restoredQuest: any | null = null;
    public dashboardQuests: any[] = [];
    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }
    get filteredCharacters() {
        return this.characters.filter((c) => c.name.toLowerCase().includes(this.searchValue.toLowerCase()));
    }
    public async bootstrap() {
        if (this.characters.length === 0) {
            await this.getCharacters();
        }
        this._restore();
    }
    private _restore() {
        const savedId = ls.get(KEY_CHAR);
        if (savedId) {
            const found = this.characters.find((c) => c.id === Number(savedId));
            if (found) {
                runInAction(() => {
                    if (!this.selectedCharacter)
                        this.selectedCharacter = found;
                    this.currentLevel = ls.get(KEY_LEVEL) ?? this.currentLevel;
                    this.currentCreatedAt = ls.get(KEY_RECORD) ?? null;
                });
            }
        }
        const savedQuest = ls.getObj<any>(KEY_QUEST_OBJ);
        if (savedQuest) {
            runInAction(() => {
                this.restoredQuest = savedQuest;
                if (!this.completedQuests.find((q) => q.ID === savedQuest.ID)) {
                    this.completedQuests = [savedQuest, ...this.completedQuests];
                }
            });
            console.log("[Store._restore] quest restored:", savedQuest.ID, "TimedPoints:", Array.isArray(savedQuest.TimedPoints)
                ? savedQuest.TimedPoints.length
                : typeof savedQuest.TimedPoints);
        }
    }
    public async getCharacters() {
        this.isLoading = true;
        try {
            const data = await CharactersApiService.getCharacters();
            const characters = mapCharacterList(data);
            runInAction(() => {
                this.characters = characters;
            });
        }
        catch (e) {
            console.error("getCharacters error:", e);
        }
        finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
    public async deleteCharacter(characterId: number): Promise<ICharacterDto | null> {
        const target = this.characters.find((character) => character.id === characterId);
        if (!target) throw new Error("Character not found");

        this.isDeletingCharacter = true;
        this.characterDeleteError = null;
        try {
            await CharactersApiService.deleteCharacter(characterId);
            const wasSelected = this.selectedCharacter?.id === characterId;
            const remaining = this.characters.filter((character) => character.id !== characterId);
            runInAction(() => {
                this.characters = remaining;
            });

            if (wasSelected) {
                this.selectCharacter(remaining[0]?.id ?? -1);
            }

            // Re-read the authoritative Atavism-backed list after the delete succeeds.
            // If refresh fails, the already-updated local list remains usable.
            await this.getCharacters();
            if (wasSelected) {
                const selectedStillExists = this.selectedCharacter
                    ? this.characters.some((character) => character.id === this.selectedCharacter?.id)
                    : false;
                if (!selectedStillExists) this.selectCharacter(this.characters[0]?.id ?? -1);
            }
            return this.selectedCharacter;
        } catch (e) {
            runInAction(() => {
                this.characterDeleteError = e instanceof Error ? e.message : String(e);
            });
            throw e;
        } finally {
            runInAction(() => {
                this.isDeletingCharacter = false;
            });
        }
    }

    public selectCharacter(characterId: number) {
        const found = this.characters.find((c) => c.id === characterId) ?? null;
        runInAction(() => {
            this.selectedCharacter = found;
            this.characterDataCSV = [];
            this.characterDataGesture = [];
            this.statResult = [];
            this.selectedCharacterDate = null;
            this.currentCreatedAt = null;
            this.currentAttempt = 1;
            this.currentLevel = "";
            this.availableLevels = [];
            this.completedQuests = [];
            this.dashboardQuests = [];
            this.minigameSessions = [];
            this.trainingCalendarSummary = {};
            this.isTrainingCalendarLoading = false;
            this.trainingCalendarError = null;
            this.trainingCalendarRequestKey = '';
            this.levelsRequestKey = '';
            this.completedQuestsRequestKey = '';
            this.minigameSessionsRequestKey = '';
            this.restoredQuest = null;
        });
        if (found) {
            ls.set(KEY_CHAR, String(found.id));
            ls.del(KEY_LEVEL);
            ls.del(KEY_RECORD);
            ls.del(KEY_QUEST_ID);
            ls.del(KEY_QUEST_OBJ);
        }
        else {
            ls.del(KEY_CHAR);
        }
    }
    public selectLevel(levelId: number | string, createdAt?: string, recordId?: string) {
        runInAction(() => {
            this.currentLevel = String(levelId);
            this.currentCreatedAt = createdAt ?? null;
        });
        ls.set(KEY_LEVEL, String(levelId));
        if (recordId)
            ls.set(KEY_RECORD, recordId);
    }

    public async loadTrainingCalendarSummary(from: string, to: string): Promise<void> {
        if (!this.selectedCharacter)
            return;

        const requestKey = `${this.selectedCharacter.id}:${from}:${to}`;
        this.trainingCalendarRequestKey = requestKey;
        this.isTrainingCalendarLoading = true;
        this.trainingCalendarError = null;

        try {
            const response = await CharactersApiService.getTrainingCalendarSummary(
                this.selectedCharacter.id,
                from,
                to,
            );

            if (this.trainingCalendarRequestKey !== requestKey)
                return;

            runInAction(() => {
                this.trainingCalendarSummary = Object.fromEntries(
                    response.days.map((day) => [day.date, day]),
                );
            });
        }
        catch (error: any) {
            console.error('[charactersStore] loadTrainingCalendarSummary error:', error);
            if (this.trainingCalendarRequestKey !== requestKey)
                return;
            runInAction(() => {
                this.trainingCalendarSummary = {};
                this.trainingCalendarError = error?.response?.data?.error
                    || error?.message
                    || 'Failed to load training calendar';
            });
        }
        finally {
            if (this.trainingCalendarRequestKey === requestKey) {
                runInAction(() => {
                    this.isTrainingCalendarLoading = false;
                });
            }
        }
    }

    public async loadMinigameSessions(characterId: number, date: string): Promise<void> {
        const requestKey = `${characterId}:${date}`;
        this.minigameSessionsRequestKey = requestKey;
        try {
            const sessions = await CharactersApiService.getMinigameSessions(characterId, date);
            if (this.minigameSessionsRequestKey !== requestKey || this.selectedCharacter?.id !== characterId)
                return;
            const firstValue = (obj: any, keys: string[]) => {
                for (const key of keys) {
                    if (obj?.[key] !== undefined &&
                        obj?.[key] !== null &&
                        obj?.[key] !== "")
                        return obj[key];
                }
                return undefined;
            };
            runInAction(() => {
                this.minigameSessions = sessions.map((s: any, i: number) => {
                    const id = String(firstValue(s, [
                        "id",
                        "ID",
                        "Id",
                        "session_id",
                        "SessionID",
                        "task_id",
                        "TaskID",
                        "quest_id",
                        "QuestID",
                    ]) ?? `minigame-${i}`);
                    const minigameType = firstValue(s, [
                        "minigame_type",
                        "MinigameType",
                        "minigameType",
                        "game_type",
                        "GameType",
                        "TaskType",
                        "task_type",
                        "type",
                        "Type",
                    ]) ?? "unknown";
                    const taskTitle = firstValue(s, [
                        "task_title",
                        "TaskTitle",
                        "title",
                        "Title",
                        "name",
                        "Name",
                    ]) ?? "Мини-игра";
                    const startTime = firstValue(s, [
                        "start_time",
                        "StartTime",
                        "started_at",
                        "StartedAt",
                        "created_at",
                        "CreatedAt",
                    ]);
                    const endTime = firstValue(s, [
                        "end_time",
                        "EndTime",
                        "completed_at",
                        "CompletedAt",
                    ]) ?? startTime;
                    return {
                        ...s,
                        id,
                        ID: firstValue(s, ["ID", "id"]) ?? id,
                        task_id: firstValue(s, ["task_id", "TaskID"]) ?? id,
                        task_title: taskTitle,
                        minigame_type: minigameType,
                        start_time: startTime,
                        end_time: endTime,
                        samples: firstValue(s, [
                            "samples",
                            "Samples",
                            "SamplesJson",
                            "samples_json",
                        ]) ?? [],
                        energy_points: firstValue(s, [
                            "energy_points",
                            "EnergyPoints",
                            "energyPoints",
                        ]) ?? [],
                        physical_activity: firstValue(s, [
                            "physical_activity",
                            "PhysicalActivity",
                            "physicalActivity",
                        ]) ?? undefined,
                        TimedPoints: firstValue(s, ["TimedPoints", "timedPoints"]) ?? [],
                    };
                });
            });
            console.log("[charactersStore] minigameSessions loaded:", this.minigameSessions);
        }
        catch (e) {
            if (this.minigameSessionsRequestKey !== requestKey || this.selectedCharacter?.id !== characterId)
                return;
            console.error("[charactersStore] loadMinigameSessions error:", e);
            runInAction(() => {
                this.minigameSessions = [];
            });
        }
    }
    public saveCurrentQuest(questId: string, questObj?: any) {
        ls.set(KEY_QUEST_ID, questId);
        const quest = questObj ?? this.completedQuests.find((q) => q.ID === questId);
        if (quest) {
            ls.setObj(KEY_QUEST_OBJ, quest);
            runInAction(() => {
                this.restoredQuest = quest;
            });
            console.log("[Store.saveCurrentQuest] saved quest:", questId, "TimedPoints:", Array.isArray(quest.TimedPoints)
                ? quest.TimedPoints.length
                : typeof quest.TimedPoints);
        }
        else {
            console.warn("[Store.saveCurrentQuest] quest not found:", questId);
        }
    }
    public getSavedQuestId(): string | null {
        return ls.get(KEY_QUEST_ID);
    }
    public async loadCharacterData(level: string) {
        if (!this.selectedCharacter)
            return;
        this.isLoading = true;
        dashboardStore.clearSelectedJoint();
        try {
            this.currentLevel = level;
            const [csv, gesture, stat] = await Promise.all([
                CharactersApiService.getCharacterCSV(this.selectedCharacter.id, level).catch((e) => (e?.response?.status === 404 ? [] : Promise.reject(e))),
                CharactersApiService.getCharacterGesture(this.selectedCharacter.id, level).catch((e) => (e?.response?.status === 404 ? [] : Promise.reject(e))),
                CharactersApiService.getCharacterStat(this.selectedCharacter.id, level).catch((e) => (e?.response?.status === 404 ? [] : Promise.reject(e))),
            ]);
            runInAction(() => {
                this.characterDataCSV = csv;
                this.characterDataGesture = gesture;
                this.statResult = stat;
            });
        }
        catch (e) {
            console.error(e);
        }
        finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
    public async loadProcessedCSVForLevel(recordId: string) {
        if (!this.selectedCharacter)
            return;
        this.isLoading = true;
        try {
            const csv = await CharactersApiService.getCharacterCSVByLevel(this.selectedCharacter.id, "", undefined, undefined, undefined, "processed", recordId);
            runInAction(() => {
                this.characterDataCSV = csv;
            });
        }
        catch (e) {
            console.error(e);
            runInAction(() => {
                this.characterDataCSV = [];
            });
        }
        finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
    public selectCsvDate(date: string) {
        if (!this.selectedCharacter)
            return;
        this.selectedCharacterDate = date;
        const f = this.selectedCharacter.csvFiles?.find((f: any) => f.csvDate === date);
        if (f) {
            runInAction(() => {
                this.characterDataCSV = f.csvData;
            });
        }
    }
    public async loadQuestsForDate(date: string) {
        if (!this.selectedCharacter)
            return;
        try {
            const data = await CharactersApiService.getCompletedQuests(this.selectedCharacter.id, date);
            runInAction(() => {
                this.dashboardQuests = data.map((q: any) => {
                    const runQuestMode = getRunQuestModeFromRecord(q);
                    const taskType = q.TaskType ?? q.task_type ?? runQuestMode;
                    return {
                        ...q,
                        TaskType: taskType,
                        task_type: q.task_type ?? q.TaskType ?? runQuestMode,
                        run_challenge_mode: q.run_challenge_mode ?? runQuestMode,
                        quest_family: q.quest_family ?? (runQuestMode ? "run" : undefined),
                        activity_family: q.activity_family ?? (runQuestMode ? "running" : undefined),
                        processing_path: q.processing_path ?? (runQuestMode ? "run_like" : undefined),
                        TimedPoints: q.TimedPoints ?? [],
                    };
                });
            });
            if (data?.length) {
                console.log("[loadQuestsForDate] raw quest[0]:", data[0]);
            }
        }
        catch (e) {
            console.error(e);
            runInAction(() => {
                this.dashboardQuests = [];
            });
        }
    }
    public async loadLevelsForDate(date = new Date().toISOString().split("T")[0]) {
        const characterId = this.selectedCharacter?.id;
        if (!characterId)
            return;
        const requestKey = `${characterId}:${date}`;
        this.levelsRequestKey = requestKey;
        this.isLoading = true;
        try {
            const { files } = await CharactersApiService.getCharacterFilesByDate(characterId, date);
            if (this.levelsRequestKey !== requestKey || this.selectedCharacter?.id !== characterId)
                return;
            if (files?.length) {
                console.log("[loadLevelsForDate] raw file[0]:", files[0]);
            }
            runInAction(() => {
                this.availableLevels = files.map((f: any, i: number) => ({
                    recordId: f.recordID || f.recordId || f.id || `${i}-${Date.now()}`,
                    level: f.level || f.Level || "unknown",
                    typeEx: f.typeEx ||
                        f.TypeEx ||
                        f.type ||
                        f.exerciseType ||
                        f.exercise_type ||
                        "unknown",
                    name: f.name ||
                        f.Name ||
                        f.title ||
                        f.Title ||
                        f.taskTitle ||
                        f.TaskTitle ||
                        `Тренировка ${new Date(f.createdAt || Date.now()).toLocaleTimeString("ru", {
                            hour: "2-digit",
                            minute: "2-digit",
                        })}`,
                    createdAt: f.createdAt || f.CreatedAt || f.date || new Date().toISOString(),
                }));
            });
        }
        catch (e) {
            if (this.levelsRequestKey !== requestKey || this.selectedCharacter?.id !== characterId)
                return;
            console.error(e);
            runInAction(() => {
                this.availableLevels = [];
            });
        }
        finally {
            if (this.levelsRequestKey === requestKey) {
                runInAction(() => {
                    this.isLoading = false;
                });
            }
        }
    }
    public async loadCompletedQuests(date: string) {
        const characterId = this.selectedCharacter?.id;
        if (!characterId)
            return;
        const requestKey = `${characterId}:${date}`;
        this.completedQuestsRequestKey = requestKey;
        this.isLoading = true;
        try {
            const data = await CharactersApiService.getCompletedQuests(characterId, date);
            if (this.completedQuestsRequestKey !== requestKey || this.selectedCharacter?.id !== characterId)
                return;
            runInAction(() => {
                const incoming = data.map((q: any) => {
                    const runQuestMode = getRunQuestModeFromRecord(q);
                    const coopMeta = getCoopMetaFromRecord(q);
                    const isCoop = isGuildCoopRecord(q);
                    const taskType = q.TaskType ?? q.task_type ?? runQuestMode;
                    return {
                        ...q,
                        ...coopMeta,
                        guild_coop: q.guild_coop ?? coopMeta.guild_coop ?? isCoop,
                        coop_session_id: q.coop_session_id ??
                            coopMeta.coop_session_id ??
                            coopMeta.guild_coop_session_id,
                        guild_coop_session_id: q.guild_coop_session_id ??
                            coopMeta.guild_coop_session_id ??
                            coopMeta.coop_session_id,
                        team_status: q.team_status ?? coopMeta.team_status,
                        team_completed: q.team_completed ?? coopMeta.team_completed,
                        team_failed: q.team_failed ?? coopMeta.team_failed,
                        TaskType: taskType,
                        task_type: q.task_type ?? q.TaskType ?? runQuestMode,
                        run_challenge_mode: q.run_challenge_mode ?? runQuestMode,
                        quest_family: q.quest_family ?? (runQuestMode || isCoop ? "run" : undefined),
                        activity_family: q.activity_family ??
                            (runQuestMode || isCoop ? "running" : undefined),
                        processing_path: q.processing_path ??
                            (runQuestMode || isCoop ? "run_like" : undefined),
                        Started: q.Started ?? false,
                        Completed: q.Completed ?? false,
                        Active: q.Active ?? true,
                        Visible: q.Visible ?? true,
                        TimedPoints: q.TimedPoints ?? [],
                    };
                });
                this.completedQuests = incoming;
                const savedQuest = this.restoredQuest;
                if (savedQuest) {
                    const fresh = incoming.find((q: any) => q.ID === savedQuest.ID);
                    if (fresh) {
                        this.restoredQuest = fresh;
                        ls.setObj(KEY_QUEST_OBJ, fresh);
                    }
                }
            });
        }
        catch (err) {
            if (this.completedQuestsRequestKey !== requestKey || this.selectedCharacter?.id !== characterId)
                return;
            console.error(err);
            runInAction(() => {
                this.completedQuests = [];
            });
        }
        finally {
            if (this.completedQuestsRequestKey === requestKey) {
                runInAction(() => {
                    this.isLoading = false;
                });
            }
        }
    }
    public deInit() {
        runInAction(() => {
            this.characters = [];
            this.selectedCharacter = null;
            this.characterDataCSV = [];
            this.characterDataGesture = [];
            this.statResult = [];
            this.currentLevel = "";
            this.currentCreatedAt = null;
            this.currentAttempt = 1;
            this.selectedCharacterDate = null;
            this.searchValue = "";
            this.availableLevels = [];
            this.completedQuests = [];
            this.dashboardQuests = [];
            this.minigameSessions = [];
            this.trainingCalendarSummary = {};
            this.isTrainingCalendarLoading = false;
            this.trainingCalendarError = null;
            this.trainingCalendarRequestKey = '';
            this.levelsRequestKey = '';
            this.completedQuestsRequestKey = '';
            this.minigameSessionsRequestKey = '';
            this.restoredQuest = null;
        });
        ls.del(KEY_CHAR);
        ls.del(KEY_LEVEL);
        ls.del(KEY_RECORD);
        ls.del(KEY_QUEST_ID);
        ls.del(KEY_QUEST_OBJ);
    }
}
export default new CharactersStore();
